import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Link,
  Tab,
  Tabs,
  Theme,
  Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import useReactRouter from 'use-react-router';
import useRouter from 'use-react-router';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import {GroupBooking, RoomParty} from '../../../graphql/groups.datatypes';
import getRoomParty from '../getRoomParty.api';
import RoomPartyEdit from './RoomPartyEdit';
import getGroupBooking from '../../GroupBookings/getGroupBooking.api';
import {WcvType} from '../../../graphql/logs.datatypes';
import AddNote from '../../Logs/AddNote/AddNote';
import RoomPartyMembersList from '../RoomPartyMembersList/RoomPartyMembersList';
import {RoomPartyMemberOptionsEdit} from '../RoomPartyMemberOptionsEdit';
import {RoomPartyOptionsEdit} from '../RoomPartyOptionsEdit';
import {LogListContent} from '../../Logs/LogList/LogList';
import {getHost} from '../../../graphql/GraphQlProvider';
import {Can} from '../../../components/Can';
import moment from 'moment';
import {checkIsAdmin} from '../../../utils/checkIsAdmin';
import {formatMoney} from '../../../utils/formatters';
import { NotesList } from '../../Notes';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  tabs: {
    marginTop: theme.spacing(3)
  },
  container: {
    marginTop: theme.spacing(3),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  card: {},
  linkButton: {
    marginRight: theme.spacing(1),
  },
    editLink: {
        textDecoration: 'underline',
    },
    editRoom: {
        display: 'flex',
    },
    editText: {
        textDecoration: 'underline',
    },
}));

const getBlankRoomParty = (groupBooking: GroupBooking | null): RoomParty => {
  return {
    __typename: 'RoomParty',
    id: -1,
    group_booking_id: groupBooking ? groupBooking.id : -1,
    hotel_resource_id: groupBooking ? groupBooking.group!.hotel_resource_id : -1,
    disabled: false,
    start_date: groupBooking ? groupBooking.start_date : undefined,
    end_date: groupBooking ? groupBooking.end_date : undefined,
    wheelchair_access: false,
    has_infants: false,
    members_different_dates: false,
    notes: '',
    booking: groupBooking ? groupBooking : undefined,
    hotel: groupBooking ? groupBooking.group!.hotel : undefined,
    members: [],
  };
};

const RoomPartyDetailsContent = (props: { roomParty: RoomParty, isAdmin: boolean, canEdit: boolean, groupRef?: string }) => {
  const classes = useStyles();
  const {roomParty, isAdmin, canEdit, groupRef} = props;
  const [tab, setTab] = useState('details');
  const [openModal, setOpenModal] = useState(false);
  const isNewParty = roomParty.id === -1;
  const {history} = useRouter();

  const showPrices = isAdmin || (moment(roomParty.end_date).add(15, 'days').diff(moment(), 'days') > 0);

  const canDownloadDocsZip = roomParty.members && roomParty.members.reduce((hasDocs, roomMember) => hasDocs || !!(roomMember.groupMember && roomMember.groupMember.documents && roomMember.groupMember.documents.length > 0), false)

  return (
      <div>

        {!isNewParty && (
            <Card className={classes.container}>
              <CardHeader
                  title={'Room Total'}
                  action={[
                    isAdmin && (<Button key={1} variant="contained" className={classes.linkButton}
                                        onClick={() => history.push('/groups/' + roomParty.booking!.group!.id)}>Back to
                      Group</Button>),
                    !isAdmin && (
                        <Can key={1} checkAll={false} permission={['groups.read', 'associated.groups.read']}><Button
                            variant="contained"
                            className={classes.linkButton}
                            onClick={() => history.push('/mygroups/' + roomParty.booking!.group!.booking_form_url)}>Back
                          to
                          Group</Button></Can>),
                    isAdmin && (<Button key={2} variant="contained" className={classes.linkButton}
                                        onClick={() => history.push('/admin_booking/' + roomParty.booking!.group!.booking_form_url + '/' + roomParty.booking!.id)}>Load
                      In Booking Tool</Button>),
                    isAdmin && canDownloadDocsZip && (<Link key={4}
                          href={getHost() + '/docs/' + btoa(JSON.stringify({'room': roomParty.id}))}>
                      <Button variant="contained">Download All Docs</Button>
                    </Link>),
                  ]}
              />
              <Divider/>
              {isAdmin && showPrices && (
                  <CardContent className={classes.card}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item lg={4} md={4} xs={12}>
                        <Typography variant="h3">Room Total:
                          {(roomParty.members && formatMoney(roomParty.members.reduce((total, member) => total + member.cost!, 0), true))}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
              )}
            </Card>
        )}

        <Tabs className={classes.tabs} onChange={(event, value) => setTab(value)} scrollButtons="auto" value={tab}
              variant="scrollable">
          <Tab label="Details" value="details"/>
          {!isNewParty && <Tab label="Members" value="members"/>}
          {isAdmin && !isNewParty && <Tab label="Guest Options" value="group_options"/>}
          {isAdmin && !isNewParty && <Tab label="Room Options" value="room_options"/>}
          {/* {isAdmin && !isNewParty && <Tab label="Notes" value="notes"/>} */}
          {isAdmin && !isNewParty && <Tab label="Logs" value="logs"/>}
        </Tabs>

        {tab === 'details' && (
            <RoomPartyEdit roomParty={roomParty} isAdmin={isAdmin} canEdit={canEdit}/>
        )}

        {tab === 'members' && (
            <RoomPartyMembersList roomParty={roomParty} isAdmin={isAdmin} canEdit={canEdit} groupRef={groupRef}/>
        )}

        {tab === 'group_options' && (
            <RoomPartyMemberOptionsEdit groupId={roomParty.booking!.group_id}
                                        roomPartyMembers={roomParty.members!} isAdmin={isAdmin} canEdit={canEdit}
                                        groupRef={groupRef}/>
        )}

        {tab === 'room_options' && (
            <RoomPartyOptionsEdit groupId={roomParty.booking!.group_id} roomParty={roomParty} isAdmin={isAdmin}
                                  canEdit={canEdit} groupRef={groupRef}/>
        )}

        {tab === 'notes' && (
            <div>
              <Button
                  color="primary"
                  onClick={() => setOpenModal(true)}
                  size="small"
                  variant="outlined"
              >
                Add Note
              </Button>
              <NotesList type={"room_party"} type_id={roomParty.id} />
              {openModal && (<AddNote onClose={() => setOpenModal(false)} type={WcvType.ROOM_PARTY}
                                      type_id={roomParty.id}/>)}
            </div>
        )}

        {tab === 'logs' && <LogListContent type={WcvType.ROOM_PARTY} type_id={roomParty.id}/>}

      </div>
  );
};


export const RoomPartyInMyGroupDetails = () => {
  const {history, match}: RouteComponentProps<{ json: string }> = useReactRouter();
  const classes = useStyles();
  const {json} = match.params;
  const jsonObj = JSON.parse(atob(json));

  const roomPartyData = getRoomParty(jsonObj.id);
  const {enqueueSnackbar} = useSnackbar();
  const group = roomPartyData.data && roomPartyData.data.roomParty && roomPartyData.data.roomParty.booking && roomPartyData.data.roomParty.booking.group;
  const title = 'Room Party Details' + (group ? ': ' + group.name : '');
  const isAdmin = checkIsAdmin();
  //TODO: add in canEdit check here i its not just for an admin.

  useEffect(() => {
    if (roomPartyData.error) {
      enqueueSnackbar(`There was an error loading Room Party`, {variant: 'error'});
      history.push('/mygroups');
    }
  }, [roomPartyData.error]);

  return (
      <Page className={classes.root} title={title}>
        <PageHeader section="groups" title={title}/>
        <LoadingOverlay loading={roomPartyData.loading}>
          {roomPartyData.data && roomPartyData.data.roomParty && (
              <RoomPartyDetailsContent roomParty={roomPartyData.data.roomParty} isAdmin={isAdmin}
                                       canEdit={isAdmin} groupRef={jsonObj.groupRef}/>)}
        </LoadingOverlay>
      </Page>
  );
};

const RoomPartyDetails = () => {
  const {history, match}: RouteComponentProps<{ id: string, booking_id?: string }> = useReactRouter();
  const classes = useStyles();
  const {id, booking_id} = match.params;
  const newRoomParty = booking_id !== undefined;
  const bookingData = booking_id ? getGroupBooking(Number(booking_id)!) : {loading: false, data: null};
  const groupBooking = bookingData && bookingData.data && bookingData.data.groupBooking ? bookingData.data.groupBooking : null;
  const roomPartyData = newRoomParty ? {
    loading: false,
    data: {roomParty: getBlankRoomParty(groupBooking)},
    error: null
  } : getRoomParty(Number(id));
  const {enqueueSnackbar} = useSnackbar();
  const group = roomPartyData.data && roomPartyData.data.roomParty && roomPartyData.data.roomParty.booking && roomPartyData.data.roomParty.booking.group;
  const title = 'Room Party Details' + (group ? ': ' + group.name : '');
  const billingParties: Record<number | string, string[]> = {};

    if (roomPartyData.data && roomPartyData.data && roomPartyData.data.roomParty && roomPartyData.data.roomParty && roomPartyData.data.roomParty.members) {
        roomPartyData.data.roomParty.members.map(member => {
            const billingPartyId = member && member.billingParty && typeof member.billingParty.id === 'number' ? member && member.billingParty && member.billingParty.id : null;
            const memberName = member && member.groupMember ? `${member.groupMember.first_name} ${member.groupMember.last_name}` : ''

            if (billingPartyId !== null) {
                if (!billingParties[billingPartyId]) {
                    billingParties[billingPartyId] = [memberName];
                } else {
                    billingParties[billingPartyId].push(memberName);
                }
            }
        })
    }

    const billingPartyList = Object.entries(billingParties).map(([billingPartyId, memberNames]) => {
        return <div className={classes.editRoom}><Link key={billingPartyId} href={'/billing_party/' + billingPartyId}><Typography variant="body2" color={'primary'} className={classes.editLink}>Billing Party - {billingPartyId} - {memberNames.join(", ")}</Typography></Link></div>
    })

  useEffect(() => {
    if (roomPartyData.error) {
      enqueueSnackbar(`There was an error loading Room Party ${id}`, {variant: 'error'});
      history.push('/room_party');
    }
  }, [roomPartyData.error]);

  return (
      <Page className={classes.root} title={title}>
          <PageHeader section={`groups ${group ? group.name : ''}`} title={title}/>
          {billingPartyList.map(party => {
              return party
          })}
        <LoadingOverlay loading={roomPartyData.loading || bookingData.loading}>
          {roomPartyData.data && roomPartyData.data.roomParty && (
              <RoomPartyDetailsContent roomParty={roomPartyData.data.roomParty} isAdmin={true} canEdit={true}/>)}
        </LoadingOverlay>
      </Page>
  );
};

export default RoomPartyDetails;

